<template>
  <div class="tableWithPerPageSelection">
    <div class="entete border-entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">
                {{ $t('parametrage.liste.' + typeParametre) }}
              </h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="is-pulled-right buttons boutonAction">
            <a class="button is-addOrEdit" @click="checkPermissions(permsParametre.maj)
          ? addEditParametrage(null) : showPopUp()"
               :class="{'button-access-no-permission' :
               !checkPermissions(permsParametre.maj) }">
              {{ $t('parametrage.addLibelle.' + typeParametre) }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="level mgt-20px">
      <div class="level-left">
        <div class="columns">
          <form @submit.prevent="$refs.tableParametrage.refresh()" class="column is-12">
            <b-input :placeholder="$t('general.button.search')"
                     type="search" icon="magnify"
                     v-model="filterInput">
            </b-input>
          </form>
        </div>
      </div>
    </div>
    <v-server-table :data="listeParametreInfoFilter" :columns="columns" :options="options"
                    url=""
                    @loaded="finLoading"
                    @loading="debutLoading"
                    ref="tableParametrage">
      <template slot='afterTable'>
        <table-items-in-view @input="$refs.tableParametrage.setLimit($event)"/>
      </template>

      <template slot="edit" slot-scope="props">
        <div @click="checkPermissions(permsParametre.supprimer)
        ? addEditParametrage(props.row) : showPopUp" class="is-cursor-pointer">
          <b-icon icon="pencil-box-outline" type="is-success"
                  :class="{ 'text-access-no-permission' :
                      !checkPermissions(permsParametre.maj) }"></b-icon>
        </div>
      </template>
      <template #archiver="{ row }">
        <btn-action-icon
          :tooltip-label="$t(`general.button.${row.actif ? 'archiver' : 'desarchiver'}`)"
          :permission="permsParametre.supprimer"
          @is-click="openPopUpArchivage(row)"
          icon-type="is-default"
          :icon="row.actif ? 'archive' : 'archive-arrow-up'">
        </btn-action-icon>
      </template>
      <template #delete="{ row }">
        <btn-action-icon
          :tooltip-label="$t('general.button.delete')"
          :permission="permsParametre.supprimer"
          @is-click="openPopUpSuppression(row.id)"
          icon-type="is-danger"
          icon="delete-forever">
        </btn-action-icon>
      </template>
    </v-server-table>
    <b-modal v-model="popUpAddEditParametre" has-modal-card class="modal">
      <pop-up-add-edit-parametre-generique
                :parametrage="selectedParametre"
                :typeParametre="typeParametre"
                :withLogo="withLogo"
                :avecCode="avecCode"
                @reloadParametrage="reloadParametrage()"/>
    </b-modal>
    <b-modal
      v-model="openPopUpDelete"
      has-modal-card
      class="modal"
      @close="closePopUp">
      <popUpPrevention
        :messHeader="$t(`parametrage.generique.${this.typeParametre}.suppression.supprimer`)"
        :messFooter="$t('general.suppression.sousTitre')"
        :cancel="$t('general.non')"
        :validate="$t('general.oui')"
        :messagesPopup="messages"
        :isErrorPopup="isError"
        @update:isError="messages = []"
        @yes="deleteParametre"
        @no="closePopUp"
      />
    </b-modal>
    <b-modal
      v-model="openPopUpArchiver"
      has-modal-card
      class="modal"
      @close="openPopUpArchiver = false">
      <popUpPrevention
        v-if="elementsToArchive.length > 0"
        :messHeader="$t(`parametrage.generique.${this.typeParametre}.archivage.${
            elementsToArchive[0].actif ? 'archiver' : 'desarchiver'
          }`)"
        :cancel="$t('general.non')"
        :validate="$t('general.oui')"
        :messagesPopup="messages"
        :isErrorPopup="isError"
        @update:isError="messages = []"
        @yes="archiverParametre"
        @no="openPopUpArchiver = false"
      />
    </b-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import inProgress from '@/services/general/inProgress';
import TableItemsInView from '@/components/general/TableItemsInView';
import breadcrumb from '@/components/general/breadcrumb';
import {
  TEXTS, RESULTS_PER_PAGE, NO_PER_PAGE_VALUE, SORT_ICON,
} from '@/configuration/clientTableConf';
import popUpAddEditParametreGenerique from '@/components/general/p_addEditParametreGenerique';
import cloneDeep from 'lodash/cloneDeep';
import genererParametrageGenerique from '@/services/parametrage/generation/genererParametrageGenerique';
import { archiver, remove } from '@/api/parametre/index';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import validation from '@/services/parametrage/validation/validationParametreGenerique';
import messageErreur from '@/services/general/messageErreur';
import messageSuccess from '@/services/general/messageSucces';
import popUpPrevention from '@/components/general/popUpPrevention';
import btnActionIcon from '@/components/general/btnActionIcon';
import { columnsSplice } from '@/services/general/columns';

const Parametres = createNamespacedHelpers('parametres');
const Permissions = createNamespacedHelpers('permissions');
const ParametrageGeneral = createNamespacedHelpers('parametrageGeneral');

export default {
  props: {
    permsParametre: {
      type: Object,
      required: true,
    },
    typeParametre: {
      type: String,
      required: true,
    },
    withLogo: {
      type: Boolean,
      required: false,
      default: false,
    },
    avecCode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    appBreadcrumb: breadcrumb,
    popUpAddEditParametreGenerique,
    TableItemsInView,
    popUpPrevention,
    btnActionIcon,
  },
  data() {
    return {
      filterInput: '',
      popUpAddEditParametre: false,
      loadingComponent: null,
      selectedParametre: {},
      parametreMarque: PARAMETRES_KEYS.marque,
      openPopUpDelete: false,
      idToDelete: null,
      openPopUpArchiver: false,
      elementsToArchive: [],
      isError: false,
      messages: [],
    };
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...ParametrageGeneral.mapGetters({
      listeParametreInfos: 'items',
      filter: 'filter',
      totalCount: 'totalCount',
    }),
    breadcrumb() {
      return [
        { title: this.$t('parametrage.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('parametrage.title'), route: { name: 'l.parametrage.general' }, isActive: false },
        { title: this.$t('parametrage.breadcrumb.' + this.typeParametre), route: { name: '' }, isActive: true },
      ];
    },
    listeParametreInfoFilter() {
      return this.listeParametreInfos.filter(item => {
        return item.libelle.toLowerCase().includes(this.filter.toLowerCase());
      });
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          libelle: this.$t('parametrage.tab.libelle'),
          ...(this.avecCode ? { code: this.$t('parametrage.tab.code') } : {}),
          edit: '',
          archiver: '',
          delete: '',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: TEXTS,
        columnsClasses: {
          edit: 'colonneSelected',
          archiver: 'colonneSelected',
          delete: 'colonneSelected',
        },
        sortable: ['libelle'],
        requestFunction: ((data) => {
          data.query = {
            auto: this.filterInput,
          };
          return this.actionsloadParametrage({ type: this.typeParametre, payload: data });
        }),
        responseAdapter: (() => {
          return {
            data: this.listeParametreInfos,
            count: this.totalCount,
          };
        }),
      };
    },
    columns() {
      const baseColumns = ['code', 'libelle', 'edit', 'archiver', 'delete'];
      if (!this.avecCode) {
        columnsSplice(baseColumns, 'code');
      }
      return baseColumns;
    },
  },
  methods: {
    inProgress(message) {
      inProgress(this.$i18n, message);
    },
    ...Parametres.mapActions({
      loadParametreForce: 'loadParametreForce',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...ParametrageGeneral.mapActions({
      actionsloadParametrage: 'loadParametrage',
    }),
    debutLoading() {
      if (this.loadingComponent) {
        this.loadingComponent.close();
      }
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    addEditParametrage(parametrage) {
      if (!parametrage) this.selectedParametre = genererParametrageGenerique();
      else this.selectedParametre = cloneDeep(parametrage);
      this.popUpAddEditParametre = true;
    },
    openPopUpSuppression(id) {
      this.idToDelete = id;
      this.isError = false;
      this.openPopUpDelete = true;
    },
    openPopUpArchivage(ligne) {
      this.isError = false;
      this.messages = [];
      this.elementsToArchive = [ligne];
      this.openPopUpArchiver = true;
    },
    recherche() {
      this.$refs.tableParametrage.refresh();
    },
    async reloadParametrage() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        await this.loadParametreForce(this.typeParametre);
        this.recherche();
      } catch (e) {
        messageErreur(this.$i18n);
      } finally {
        loadingComponent.close();
      }
    },
    async archiverParametre() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        if (this.elementsToArchive.length === 0) return;
        this.isError = false;
        this.messages = [];
        const payload = {
          archiver: this.elementsToArchive[0].actif,
          ids: this.elementsToArchive.map(e => e.id),
        };
        await archiver(this.typeParametre, payload);
        await this.loadParametreForce(this.typeParametre);
        this.openPopUpArchiver = false;
        messageSuccess(this.$t(`parametrage.generique.${this.typeParametre}.archivage.${payload.archiver ? 'successArchiver' : 'successDesarchiver'}`));
        this.recherche();
      } catch (e) {
        messageErreur(this.$i18n);
      } finally {
        loadingComponent.close();
      }
    },
    async deleteParametre() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        this.isError = false;
        this.messages = [];
        await remove(this.typeParametre, this.idToDelete);
        await this.loadParametreForce(this.typeParametre);
        this.closePopUp();
        messageSuccess(this.$t(`parametrage.generique.${this.typeParametre}.suppression.successSupprimer`));
        this.$refs.tableParametrage.refresh();
        loadingComponent.close();
      } catch (e) {
        if (e.response && e.response.data.error.validationErrors) {
          validation(e.response, this.messages);
          this.isError = true;
          loadingComponent.close();
          return;
        }
        messageErreur(this.$i18n);
        loadingComponent.close();
        throw e;
      }
    },
    closePopUp() {
      this.messages = [];
      this.openPopUpDelete = false;
    },
  },
  mounted() {
    this.filterInput = this.filter;
  },
};

</script>
